import { z } from 'zod'
import type { Erc20Transfer, Hex } from '../../types'
import { addressPrimitiveSchema } from '../../types/primitive'
import type { TransactionIndex } from '../../types/transactionIndex'
import type { KaiascanClient } from './client'
import { kaiascanTokenTransferSchema } from './schema'

type GetTokenTransferEventsByAddressParameters = {
  client: KaiascanClient
  address: Hex
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: Record<string, string>
}

const resultSchema = z.array(kaiascanTokenTransferSchema)

/**
 * Kaiascan から address の token transfer 一覧を取得する。
 *
 * @param parameters - client、address、block range、pagination、headers。
 * @returns transaction index と ERC20 transfer の配列。
 */
export const getTokenTransferEventsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 500,
  sort = 'asc',
  headers,
}: GetTokenTransferEventsByAddressParameters): Promise<{
  indexes: TransactionIndex[]
  erc20Transfers: Erc20Transfer[]
}> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'tokentx',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined ? {} : { endblock: endblock.toString() }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    const indexes: TransactionIndex[] = []
    const erc20Transfers: Erc20Transfer[] = []
    for (const transfer of result) {
      indexes.push({
        blockNumber: transfer.blockNumber,
        hash: transfer.hash,
        timestamp: transfer.timestamp,
      })
      const contractAddress = addressPrimitiveSchema.safeParse(
        transfer.contractAddress,
      )
      if (contractAddress.success) {
        erc20Transfers.push({
          address: contractAddress.data,
          from: transfer.from,
          logIndex: transfer.logIndex,
          to: transfer.to,
          value: transfer.value,
        })
      }
    }
    return {
      indexes,
      erc20Transfers,
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('No transactions found')
    ) {
      return {
        indexes: [],
        erc20Transfers: [],
      }
    }
    throw error
  }
}
